import { FormikErrors, useFormik } from 'formik';
import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as LineSVG } from 'assets/images/icons/line.svg';
import { ReactComponent as MobileLineSVG } from 'assets/images/icons/mobile_line.svg';
import { ReactComponent as ObjectSensorLineSVG } from 'assets/images/icons/object_sensor_line.svg';
import { ReactComponent as ObjectSensorMobileLineSVG } from 'assets/images/icons/object_sensor_mobile_line.svg';

import { Button } from 'ui';

import useSize from 'hooks/useSize';

import ObjectDetails from './ObjectDetails';
import SensorDetails from './SensorDetails';
import ZoneDetails from './ZoneDetails';

import { authApi } from 'services/auth';
import { OBJECT_SENSOR_TYPES, QUERY_PARAMS, ZONE_SENSOR_TYPES } from 'utils/constants';
import { getDialCode } from 'utils/countries';
import { QueryHelper } from 'utils/helpers';
import { normalizeKey } from 'utils/helpers/appkey';
import {
  loadAllUserCreateFiles,
  loadAllUserUpdateFiles,
  loadFileFromIndexedDB,
  removeFileFromIndexedDB,
  saveFileToIndexedDB,
} from 'utils/indexedDB';
import { PAGES } from 'utils/link';
import { validationSchemaStepTwo } from './validationSchema';

import useValidation from 'hooks/useValidation';

import {
  detailLineMarginLeft,
  detailMobileLineMarginLeft,
  gap,
  initialStepTwoSensors,
  initialStepTwoValues,
  initialStepTwoZones,
} from './constant';

import { ModalType } from 'entities/common';
import { IUserCreateDetails, IUserCreateSensors, IUserCreateZones } from 'entities/view/userCreate';
import { showModal } from 'store/Modal/Modal.action';
import { IRootStore } from 'store/Root.reducer';
import { setStepTwoData as sendStepTwoData } from 'store/UserCreate/UserCreate.action';
import {
  setStepTwoData as editStepTwoData,
  resetUserUpdateData,
  saveFormDataToStorage,
} from 'store/UserUpdate/UserUpdate.action';

type PropTypes = {
  id?: string;
};

const UserCreateStepTwo: FC<PropTypes> = ({ id }) => {
  const formRef = useRef<null | HTMLFormElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: IRootStore) => state[id ? 'userUpdate' : 'userCreate']);
  const userId = userData?.userId;
  const [loadedFiles, setLoadedFiles] = useState<Record<string, File>>({});
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMobile } = useSize();

  const form = useFormik({
    initialValues: userData.stepTwo,
    validationSchema: validationSchemaStepTwo,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      if (isSubmitting) return;

      setIsSubmitting(true);

      if (!id) {
        if (!userId) {
          navigate(PAGES.USER_CREATE + QueryHelper.getQueryString({ [QUERY_PARAMS.STEP]: 1 }), {
            replace: true,
          });
          setIsSubmitting(false);
          return;
        }

        let attachData: any = undefined;
        try {
          const missingFilePromises = values.details.map(async (detail, index) => {
            const fileId = `object-${index}`;

            if (!loadedFiles[fileId] && !detail.file) {
              try {
                const file = await loadFileFromIndexedDB(fileId);

                if (file) {
                  setLoadedFiles((prev) => ({ ...prev, [fileId]: file }));
                  return true;
                }
              } catch (error) {
                console.error(`Failed to load file ${fileId}:`, error);
              }
            }
            return false;
          });

          await Promise.all(missingFilePromises);
          await new Promise((resolve) => setTimeout(resolve, 100));

          const hasFileErrors = Object.keys(fileErrors).length > 0;
          if (hasFileErrors) {
            return;
          }

          await form.validateForm();

          if (!form.isValid) {
            return;
          }

          dispatch(sendStepTwoData(values));

          const objectsWithFileIds = await Promise.all(
            values.details.map(async (detail, index) => {
              const fileId = `object-${index}`;
              const file = loadedFiles[fileId] || detail.file;
              let s3FileId = undefined;

              if (file) {
                const uploadResponse = await authApi.uploadFile(file);
                s3FileId = uploadResponse.file_id;
              }

              const zones = await Promise.all(
                detail.zones.map(async (zone) => {
                  let zonePhotoFileId = undefined;
                  let zonePhotoOriginalFilename = undefined;

                  if (zone.photo) {
                    const photoUpload = await authApi.uploadFile(zone.photo);
                    zonePhotoFileId = photoUpload.file_id;
                    zonePhotoOriginalFilename = zone.photo.name;
                  }

                  return {
                    name: zone.name,
                    plant_type: zone.plantType,
                    plant_name: zone.plantName,
                    soil_type: zone.soilType,
                    photo_file_id: zonePhotoFileId,
                    photo_original_filename: zonePhotoOriginalFilename,
                    sensors: (zone.sensors || []).map((sensor) => ({
                      sensor_type: sensor.type,
                      sensor_eui: sensor.sensorEui,
                      sensor_device_code: normalizeKey(sensor.sensorKey),
                    })),
                  };
                }),
              );

              const sensors = (detail.sensors || []).map((sensor) => ({
                sensor_type: sensor.type,
                sensor_eui: sensor.sensorEui,
                sensor_device_code: normalizeKey(sensor.sensorKey),
              }));

              return {
                name: detail.name,
                file_id: s3FileId,
                zones,
                sensors,
              };
            }),
          );

          attachData = {
            user_id: userId,
            objects: objectsWithFileIds,
          };

          await authApi.attachHierarchy(attachData);

          navigate(PAGES.ADMIN_USERS, { replace: true });
        } catch (error: any) {
          console.error('Failed to attach hierarchy:', error);
          if (attachData) {
            handleApiError(error);
          }
        } finally {
          setIsSubmitting(false);
        }
      } else {
        let updateData: any = undefined;
        try {
          const fileMap: Record<string, File> = { ...loadedFiles };

          const missingFilePromises = values.details.map(async (detail, index) => {
            const fileKey = userId ? `object-${userId}-${index}` : `object-${index}`;

            if (!fileMap[fileKey] && !detail.file && !detail.fileId && !detail.originalFilename) {
              try {
                const file = await loadFileFromIndexedDB(fileKey);
                if (file) {
                  fileMap[fileKey] = file;
                }
              } catch (error) {
                console.error(`Failed to load file ${fileKey}:`, error);
              }
            }
          });

          await Promise.all(missingFilePromises);
          setLoadedFiles(fileMap);

          const hasFileErrors = Object.keys(fileErrors).length > 0;
          if (hasFileErrors) {
            setIsSubmitting(false);
            return;
          }

          const errors = await form.validateForm();

          if (Object.keys(errors).length > 0) {
            setIsSubmitting(false);
            return;
          }

          dispatch(editStepTwoData(values));

          const objectsWithFileIds = await Promise.all(
            values.details.map(async (detail, index) => {
              try {
                let fileIdToUse: string | undefined = undefined;

                const fileKey = userId ? `object-${userId}-${index}` : `object-${index}`;

                const file = detail.file || fileMap[fileKey];

                if (file) {
                  const uploadResponse = await authApi.uploadFile(file);
                  fileIdToUse = uploadResponse.file_id;
                } else if (detail.fileId) {
                  fileIdToUse = detail.fileId;
                }

                const zones = await Promise.all(
                  detail.zones.map(async (zone) => {
                    let zonePhotoFileId = zone.photoFileId || undefined;
                    let zonePhotoOriginalFilename = zone.photoOriginalFilename || undefined;

                    if (zone.photo) {
                      const photoUpload = await authApi.uploadFile(zone.photo);
                      zonePhotoFileId = photoUpload.file_id;
                      zonePhotoOriginalFilename = zone.photo.name;
                    }

                    return {
                      id: zone.id,
                      name: zone.name,
                      plant_type: zone.plantType,
                      plant_name: zone.plantName,
                      soil_type: zone.soilType,
                      photo_file_id: zonePhotoFileId || '',
                      photo_original_filename: zonePhotoOriginalFilename || '',
                      sensors: (zone.sensors || []).map((sensor) => ({
                        id: sensor.id,
                        sensor_type: sensor.type,
                        sensor_eui: sensor.sensorEui,
                        ...(sensor.id ? {} : { sensor_device_code: normalizeKey(sensor.sensorKey) }),
                        active: sensor.active ?? true,
                      })),
                    };
                  }),
                );

                const sensors = (detail.sensors || []).map((sensor) => ({
                  id: sensor.id,
                  sensor_type: sensor.type,
                  sensor_eui: sensor.sensorEui,
                  ...(sensor.id ? {} : { sensor_device_code: normalizeKey(sensor.sensorKey) }),
                  active: sensor.active ?? true,
                }));

                return {
                  id: detail.id,
                  name: detail.name,
                  ...(!!fileIdToUse && { file_id: fileIdToUse }),
                  zones,
                  sensors,
                };
              } catch (error) {
                throw error;
              }
            }),
          );

          const whatsappNumber = userData.stepOne.phoneNumber
            ? getDialCode(userData.stepOne.phoneCode) +
              userData.stepOne.phoneNumber.replace(/\D/g, '')
            : undefined;

          updateData = {
            id: parseInt(id!),
            name: userData.stepOne.fullname,
            email: userData.stepOne.email,
            ...(userData.stepOne.password && {
              password: userData.stepOne.password,
            }),
            whatsapp_number: whatsappNumber,
            whatsapp_notifications_enabled: userData.stepOne.whatsappNotifications,
            objects: objectsWithFileIds,
          };

          await authApi.bulkUpdateUser(updateData);

          navigate(PAGES.ADMIN_USERS, { replace: true });
          dispatch(resetUserUpdateData());
        } catch (error: any) {
          console.error('Failed to update user:', error);
          if (updateData) {
            handleApiError(error);
          }
        } finally {
          setIsSubmitting(false);
        }
      }
    },
  });

  useValidation(formRef, form.errors, form.submitCount);

  const handleApiError = useCallback(
    (error: any) => {
      let actualErrorData = error?.response?.data;

      if (!actualErrorData && error?.data) actualErrorData = error.data;
      if (!actualErrorData && Array.isArray(error)) actualErrorData = error;
      if (!actualErrorData && error && typeof error === 'object') {
        const validationErrors = Object.values(error).filter(
          (v: any) => v && typeof v === 'object' && v.detail,
        );
        if (validationErrors.length > 0) {
          actualErrorData = validationErrors;
        } else if (Object.keys(error).every((key) => !isNaN(Number(key)))) {
          actualErrorData = Object.values(error);
        }
      }

      const normalizedErrors = Array.isArray(actualErrorData)
        ? actualErrorData
        : actualErrorData
          ? [actualErrorData]
          : [];

      normalizedErrors.forEach((err) => {
        const field = err?.detail?.field || err?.field;
        const detail =
          err?.detail?.detail || (typeof err?.detail === 'string' ? err.detail : err?.message);
        const value = err?.detail?.value || err?.value;

        if (field && detail) {
          const fieldMapping: Record<string, string> = {
            sensorEui: 'sensorEui',
            sensor_eui: 'sensorEui',
            sensor_type: 'type',
            sensorType: 'type',
          };

          const formField = fieldMapping[field] || field;

          form.values.details?.forEach((detailItem, detailIndex) => {
            detailItem.sensors?.forEach((sensor, sensorIndex) => {
              const sensorValue = sensor[formField as keyof typeof sensor];
              const isEuiMatch =
                sensor.sensorEui &&
                value &&
                String(sensor.sensorEui).toLowerCase() === String(value).toLowerCase();

              if (sensorValue === value || isEuiMatch) {
                const fieldPath = `details[${detailIndex}].sensors[${sensorIndex}].${formField}`;
                form.setFieldError(fieldPath, detail);
              }
            });

            detailItem.zones?.forEach((zone, zoneIndex) => {
              zone.sensors?.forEach((sensor, sensorIndex) => {
                const sensorValue = sensor[formField as keyof typeof sensor];
                const isEuiMatch =
                  sensor.sensorEui &&
                  value &&
                  String(sensor.sensorEui).toLowerCase() === String(value).toLowerCase();

                if (sensorValue === value || isEuiMatch) {
                  const fieldPath = `details[${detailIndex}].zones[${zoneIndex}].sensors[${sensorIndex}].${formField}`;
                  form.setFieldError(fieldPath, detail);
                }
              });
            });
          });
        }
      });
    },
    [form],
  );

  const handleAddNewObject = () => {
    const newDetails = [...form.values.details, ...initialStepTwoValues];
    form.setFieldValue('details', newDetails);
  };

  const handleRemoveObject = (detailIndex: number) => {
    if (form.values.details?.length <= 1) {
      return;
    }

    dispatch(
      showModal({
        modalType: ModalType.OBJECT_DELETE,
        modalProps: {
          onConfirm: async () => {
            try {
              const fileId = userId ? `object-${userId}-${detailIndex}` : `object-${detailIndex}`;

              if (loadedFiles[fileId]) {
                await removeFileFromIndexedDB(fileId);
                setLoadedFiles((prev) => {
                  const newFiles = { ...prev };
                  delete newFiles[fileId];
                  return newFiles;
                });
              }

              const newDetails = form.values.details?.filter((_, index) => index !== detailIndex);
              form.setFieldValue('details', newDetails);

              setFileErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[detailIndex];
                return newErrors;
              });
            } catch (error) {
              console.error('Failed to remove object:', error);
            }
          },
        },
      }),
    );
  };

  const handleObjectNameChange = (detailIndex: number, value: string) => {
    if (value.length > 100) {
      value = value.substring(0, 100);
    }
    form.setFieldValue(`details[${detailIndex}].name`, value);
    form.setFieldError(`details[${detailIndex}].name`, undefined);
  };

  const handleFileUpload = (detailIndex: number) => async (file: File) => {
    if (!file) return;

    const allowedFormats = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const allowedExtensions = ['.doc', '.docx'];

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const isValidFormat =
      allowedFormats.includes(file.type) || allowedExtensions.includes(fileExtension);

    const maxSize = 10 * 1024 * 1024;
    const isValidSize = file.size <= maxSize;

    if (!isValidFormat || !isValidSize) {
      const errorMessage = 'Invalid file format or file size. Please upload a DOC or DOCX, 10MB.';
      setFileErrors((prev) => ({ ...prev, [detailIndex]: errorMessage }));
      return;
    }

    setFileErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[detailIndex];
      return newErrors;
    });

    try {
      const fileId = userId ? `object-${userId}-${detailIndex}` : `object-${detailIndex}`;
      await saveFileToIndexedDB(fileId, file);

      const newArray = [...form.values.details];
      newArray[detailIndex].file = file;

      form.setFieldValue('details', newArray);
      setLoadedFiles((prev) => ({ ...prev, [fileId]: file }));
    } catch (error) {
      console.error('Failed to save file to IndexedDB:', error);
    }
  };

  const handleFileRemove = (detailIndex: number) => () => {
    dispatch(
      showModal({
        modalType: ModalType.QUESTIONNAIRE_DELETE,
        modalProps: {
          onConfirm: async () => {
            try {
              const fileId = userId ? `object-${userId}-${detailIndex}` : `object-${detailIndex}`;
              await removeFileFromIndexedDB(fileId);

              const newArray = [...form.values.details];
              newArray[detailIndex].file = undefined;

              if (userId) {
                newArray[detailIndex].originalFilename = undefined;
                newArray[detailIndex].fileId = undefined;
              }

              form.setFieldValue('details', newArray);
              setLoadedFiles((prev) => {
                const newFiles = { ...prev };
                delete newFiles[fileId];
                return newFiles;
              });

              setFileErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[detailIndex];
                return newErrors;
              });
            } catch (error) {
              console.error('Failed to remove file from IndexedDB:', error);
            }
          },
        },
      }),
    );
  };

  const handleAddObjectSensor = (detailIndex: number) => {
    const newList = [...form.values.details];
    const sensors = [...(newList[detailIndex]?.sensors || []), ...initialStepTwoSensors];
    form.setFieldValue(`details[${detailIndex}].sensors`, sensors);
  };

  const handleRemoveObjectSensor = (detailIndex: number, sensorIndex: number) => {
    dispatch(
      showModal({
        modalType: ModalType.SENSOR_DELETE,
        modalProps: {
          onConfirm: () => {
            const newList = [...form.values.details];
            const sensors = [...(newList[detailIndex]?.sensors || [])].filter(
              (_, index) => index !== sensorIndex,
            );

            form.setFieldValue(`details[${detailIndex}].sensors`, sensors);
            form.setErrors({});
          },
        },
      }),
    );
  };

  const handleObjectSensorChange = (
    detailIndex: number,
    sensorIndex: number,
    field: string,
    value: string | boolean,
  ) => {
    form.setFieldValue(`details[${detailIndex}].sensors[${sensorIndex}].${field}`, value);
    form.setFieldError(`details[${detailIndex}].sensors[${sensorIndex}].${field}`, undefined);
  };

  const handleAddNewZone = (detailIndex: number) => {
    const newList = [...form.values.details];
    const zones = [...(newList[detailIndex]?.zones || []), ...initialStepTwoZones];
    form.setFieldValue(`details[${detailIndex}].zones`, zones);
    form.setErrors({});
  };

  const handleRemoveZone = (detailIndex: number, zoneIndex: number) => {
    const currentZones = form.values.details?.[detailIndex]?.zones || [];
    if (currentZones.length <= 1) {
      return;
    }

    dispatch(
      showModal({
        modalType: ModalType.ZONE_DELETE,
        modalProps: {
          onConfirm: () => {
            const newList = [...form.values.details];
            const zones = [...(newList[detailIndex]?.zones || [])].filter(
              (_, index) => index !== zoneIndex,
            );
            form.setFieldValue(`details[${detailIndex}].zones`, zones);
            form.setErrors({});
          },
        },
      }),
    );
  };

  const handleZoneFieldChange = (
    detailIndex: number,
    zoneIndex: number,
    field: string,
    value: string,
  ) => {
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].${field}`, value);
    form.setFieldError(`details[${detailIndex}].zones[${zoneIndex}].${field}`, undefined);
  };

  const handleZonePhotoUpload = (detailIndex: number, zoneIndex: number, file: File) => {
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].photo`, file);
  };

  const handleZonePhotoRemove = (detailIndex: number, zoneIndex: number) => {
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].photo`, undefined);
    form.setFieldValue(
      `details[${detailIndex}].zones[${zoneIndex}].photoOriginalFilename`,
      undefined,
    );
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].photoUrl`, undefined);
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].photoFileId`, undefined);
  };

  const handleAddZoneSensor = (detailIndex: number, zoneIndex: number) => {
    const sensors = [
      ...(form.values.details[detailIndex]?.zones[zoneIndex]?.sensors || []),
      ...initialStepTwoSensors,
    ];
    form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].sensors`, sensors);
  };

  const handleRemoveZoneSensor = (detailIndex: number, zoneIndex: number, sensorIndex: number) => {
    dispatch(
      showModal({
        modalType: ModalType.SENSOR_DELETE,
        modalProps: {
          onConfirm: () => {
            const sensors = [
              ...(form.values.details[detailIndex]?.zones[zoneIndex]?.sensors || []),
            ].filter((_, index) => index !== sensorIndex);

            form.setFieldValue(`details[${detailIndex}].zones[${zoneIndex}].sensors`, sensors);
            form.setErrors({});
          },
        },
      }),
    );
  };

  const handleZoneSensorChange = (
    detailIndex: number,
    zoneIndex: number,
    sensorIndex: number,
    field: string,
    value: string | boolean,
  ) => {
    form.setFieldValue(
      `details[${detailIndex}].zones[${zoneIndex}].sensors[${sensorIndex}].${field}`,
      value,
    );
    form.setFieldError(
      `details[${detailIndex}].zones[${zoneIndex}].sensors[${sensorIndex}].${field}`,
      undefined,
    );
  };

  const getObjectNameError = (detailIndex: number): string | undefined => {
    const error = (form.errors.details?.[detailIndex] as FormikErrors<IUserCreateDetails>)?.name;
    return error ? String(error) : undefined;
  };

  const getObjectSensorError = (
    detailIndex: number,
    sensorIndex: number,
    key: keyof IUserCreateSensors,
  ): string | undefined => {
    const error = (
      (form.errors.details?.[detailIndex] as FormikErrors<IUserCreateDetails>)?.sensors?.[
        sensorIndex
      ] as FormikErrors<IUserCreateSensors>
    )?.[key];
    return error ? String(error) : undefined;
  };

  const getZoneFieldError = (
    detailIndex: number,
    zoneIndex: number,
    key: keyof IUserCreateZones,
  ): string | undefined => {
    const error = (
      (form.errors.details?.[detailIndex] as FormikErrors<IUserCreateDetails>)?.zones?.[
        zoneIndex
      ] as FormikErrors<IUserCreateZones>
    )?.[key];
    return error ? String(error) : undefined;
  };

  const getZoneSensorError = (
    detailIndex: number,
    zoneIndex: number,
    sensorIndex: number,
    key: keyof IUserCreateSensors,
  ): string | undefined => {
    const zoneErrors = (form.errors.details?.[detailIndex] as FormikErrors<IUserCreateDetails>)
      ?.zones?.[zoneIndex] as FormikErrors<IUserCreateZones>;
    const error = (zoneErrors?.sensors?.[sensorIndex] as FormikErrors<IUserCreateSensors>)?.[key];
    return error ? String(error) : undefined;
  };

  useEffect(() => {
    const loadFiles = async () => {
      try {
        const files = await (userId ? loadAllUserUpdateFiles(userId) : loadAllUserCreateFiles());
        setLoadedFiles(files);
      } catch (error) {
        console.error('Failed to load files from IndexedDB:', error);
      }
    };
    loadFiles();
  }, [userId]);

  useEffect(() => {
    if (form.values && !!id && userId === parseInt(id)) {
      dispatch(saveFormDataToStorage(form.values));
    }
  }, [dispatch, form.values, id, userId]);

  return (
    <form ref={formRef} onSubmit={form.handleSubmit} className="user-create__step-two">
      <div id={'step-two__board'} className="step-two__board">
        {form.values?.details?.map((detail, detailIndex) => (
          <Fragment key={detailIndex}>
            <div>
              <ObjectDetails
                detail={{
                  ...detail,
                  file: loadedFiles[`object-${detailIndex}`] || detail.file,
                }}
                detailIndex={detailIndex}
                onFileUpload={handleFileUpload}
                onFileRemove={handleFileRemove(detailIndex)}
                onAddNewZone={handleAddNewZone}
                onAddObjectSensor={handleAddObjectSensor}
                onRemoveObject={form.values?.details?.length > 1 ? handleRemoveObject : undefined}
                onNameChange={handleObjectNameChange}
                error={getObjectNameError(detailIndex)}
                fileError={fileErrors[detailIndex]}
              />

              <div
                className="line"
                style={{
                  height: getDetailLineHeight(form.values?.details, detailIndex, detail),
                  marginLeft: isMobile ? detailMobileLineMarginLeft : detailLineMarginLeft,
                }}
              />
            </div>

            {detail.sensors?.map((sensor, sensorIndex) => (
              <div
                key={`obj-sensor-${detailIndex}-${sensorIndex}`}
                className="step-two__object-sensor-wrapper"
              >
                <div className="child-line">
                  {isMobile ? <ObjectSensorMobileLineSVG /> : <ObjectSensorLineSVG />}
                </div>

                <SensorDetails
                  id={id}
                  title="Object sensor"
                  errorFieldPrefix={`details[${detailIndex}].sensors[${sensorIndex}]`}
                  sensor={sensor}
                  sensorIndex={sensorIndex}
                  detailIndex={detailIndex}
                  sensorTypes={OBJECT_SENSOR_TYPES}
                  onRemoveSensor={handleRemoveObjectSensor}
                  onSensorChange={handleObjectSensorChange}
                  setError={form.setFieldError}
                  sensorTypeError={getObjectSensorError(detailIndex, sensorIndex, 'type')}
                  sensorIdError={getObjectSensorError(detailIndex, sensorIndex, 'sensorEui')}
                  sensorKeyError={getObjectSensorError(detailIndex, sensorIndex, 'sensorKey')}
                />
              </div>
            ))}

            {detail.zones?.map((zone, zoneIndex) => (
              <Fragment key={`zone-${detailIndex}-${zoneIndex}`}>
                <div>
                  <div className="child-line">{isMobile ? <MobileLineSVG /> : <LineSVG />}</div>

                  <ZoneDetails
                    zone={zone}
                    zoneIndex={zoneIndex}
                    detailIndex={detailIndex}
                    hasSensor={!!zone.sensors?.length}
                    onAddZoneSensor={handleAddZoneSensor}
                    onRemoveZone={detail.zones?.length > 1 ? handleRemoveZone : undefined}
                    onFieldChange={handleZoneFieldChange}
                    onPhotoUpload={handleZonePhotoUpload}
                    onPhotoRemove={handleZonePhotoRemove}
                    nameError={getZoneFieldError(detailIndex, zoneIndex, 'name') as string}
                    plantTypeError={
                      getZoneFieldError(detailIndex, zoneIndex, 'plantType') as string
                    }
                    plantNameError={
                      getZoneFieldError(detailIndex, zoneIndex, 'plantName') as string
                    }
                    soilTypeError={getZoneFieldError(detailIndex, zoneIndex, 'soilType') as string}
                  />

                  {zone.sensors?.length > 0 && (
                    <div
                      className="zone-line line"
                      style={{
                        height: getZoneLineHeight(form.values?.details, detailIndex, zoneIndex),
                        marginLeft: isMobile ? detailMobileLineMarginLeft : detailLineMarginLeft,
                      }}
                    />
                  )}
                </div>

                {zone.sensors?.map((sensor, sensorIndex) => (
                  <div
                    key={`zone-sensor-${detailIndex}-${zoneIndex}-${sensorIndex}`}
                    className="step-two__zone-sensor-wrapper"
                  >
                    <div className="zone-child-line">
                      {isMobile ? <MobileLineSVG /> : <LineSVG />}
                    </div>

                    <SensorDetails
                      id={id}
                      title="Zone sensor"
                      errorFieldPrefix={`details[${detailIndex}].zones[${zoneIndex}].sensors[${sensorIndex}]`}
                      sensor={sensor}
                      sensorIndex={sensorIndex}
                      detailIndex={detailIndex}
                      sensorTypes={ZONE_SENSOR_TYPES}
                      onRemoveSensor={(dIdx, sIdx) => handleRemoveZoneSensor(dIdx, zoneIndex, sIdx)}
                      onSensorChange={(dIdx, sIdx, field, value) =>
                        handleZoneSensorChange(dIdx, zoneIndex, sIdx, field, value)
                      }
                      setError={form.setFieldError}
                      sensorTypeError={getZoneSensorError(
                        detailIndex,
                        zoneIndex,
                        sensorIndex,
                        'type',
                      )}
                      sensorIdError={getZoneSensorError(
                        detailIndex,
                        zoneIndex,
                        sensorIndex,
                        'sensorEui',
                      )}
                      sensorKeyError={getZoneSensorError(
                        detailIndex,
                        zoneIndex,
                        sensorIndex,
                        'sensorKey',
                      )}
                    />
                  </div>
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>

      {typeof form.errors.details === 'string' && (
        <div className="step-two__error">{form.errors.details}</div>
      )}

      <div className="step-two__actions">
        <Button
          onClick={handleAddNewObject}
          fullWidth
          size="medium"
          variant="secondary"
          type="button"
        >
          Add new object
        </Button>

        <Button type="submit" fullWidth size="medium" disabled={isSubmitting}>
          {!id ? (isSubmitting ? 'Creating...' : 'Save') : ''}
          {id ? (isSubmitting ? 'Updating...' : 'Update') : ''}
        </Button>
      </div>
    </form>
  );
};

function getDetailLineHeight(
  fullData: IUserCreateDetails[],
  index: number,
  data: IUserCreateDetails,
) {
  const zonesCount = data.zones?.length || 0;
  const objectSensorsCount = data.sensors?.length || 0;
  const directChildrenCount = objectSensorsCount + zonesCount;

  if (directChildrenCount <= 1) return 0;

  const board = document.getElementById('step-two__board');
  if (!board) return 0;

  const prevData = fullData?.filter((_, dataIndex) => dataIndex < index);
  const prevZones = prevData
    ?.map((item) => item.zones?.length || 0)
    ?.reduce((acc, curr) => acc + curr, 0);
  const prevZoneSensors = prevData
    ?.map((item) => item.zones?.reduce((a, z) => a + (z.sensors?.length || 0), 0) || 0)
    ?.reduce((acc, curr) => acc + curr, 0);
  const prevObjectSensors = prevData
    ?.map((item) => item.sensors?.length || 0)
    ?.reduce((acc, curr) => acc + curr, 0);

  const allZoneElements = [...(board.getElementsByClassName('step-two__zone') || [])];
  const allZoneSensorWrappers = [
    ...(board.getElementsByClassName('step-two__zone-sensor-wrapper') || []),
  ];

  const allSensorCards = [...(board.getElementsByClassName('step-two__sensor') || [])];
  const objectSensorCards = allSensorCards.filter(
    (el) => !!el.closest('.step-two__object-sensor-wrapper'),
  );

  const myObjectSensorHeights = objectSensorCards
    .slice(prevObjectSensors, prevObjectSensors + objectSensorsCount)
    .map((el) => el.getClientRects()[0]?.height || 0);

  const myZoneHeights = allZoneElements
    .slice(prevZones, prevZones + zonesCount)
    .map((el) => el.getClientRects()[0]?.height || 0);

  const zoneSensorsCount =
    data.zones?.reduce((acc, zone) => acc + (zone.sensors?.length || 0), 0) || 0;
  const myZoneSensorHeights = allZoneSensorWrappers
    .slice(prevZoneSensors, prevZoneSensors + zoneSensorsCount)
    .map((el) => el.getClientRects()[0]?.height || 0);

  const allChildHeights: number[] = [...myObjectSensorHeights];

  let zoneSensorOffset = 0;
  data.zones?.forEach((zone, zi) => {
    allChildHeights.push(myZoneHeights[zi] || 0);
    const zSensors = zone.sensors?.length || 0;
    const isLastZone = zi === zonesCount - 1;

    if (!isLastZone) {
      for (let si = 0; si < zSensors; si++) {
        allChildHeights.push(myZoneSensorHeights[zoneSensorOffset + si] || 0);
      }
    }
    zoneSensorOffset += zSensors;
  });

  if (allChildHeights.length <= 1) return 0;

  const allExceptLast = allChildHeights.slice(0, -1);
  const height =
    allExceptLast.reduce((acc, curr) => acc + curr, 0) + (allChildHeights.length - 1) * gap + 2;

  return height;
}

function getZoneLineHeight(fullData: IUserCreateDetails[], detailIndex: number, zoneIndex: number) {
  const data = fullData[detailIndex];
  const zoneSensors = data.zones?.[zoneIndex]?.sensors || [];
  if (zoneSensors.length <= 0) return 0;

  const board = document.getElementById('step-two__board');
  if (!board) return 0;

  const prevObjectsZoneSensors = fullData
    .filter((_, i) => i < detailIndex)
    .reduce((acc, d) => acc + (d.zones?.reduce((a, z) => a + (z.sensors?.length || 0), 0) || 0), 0);

  let prevZonesInThisObject = 0;
  for (let i = 0; i < zoneIndex; i++) {
    prevZonesInThisObject += data.zones?.[i]?.sensors?.length || 0;
  }

  const totalPrev = prevObjectsZoneSensors + prevZonesInThisObject;

  const zoneSensorWrappers = [
    ...(board.getElementsByClassName('step-two__zone-sensor-wrapper') || []),
  ];
  const myWrappers = zoneSensorWrappers.slice(totalPrev, totalPrev + zoneSensors.length);
  const heights = myWrappers.map((el) => el.getClientRects()[0]?.height || 0);

  if (heights.length <= 0) return 0;

  const allExceptLast = heights.slice(0, -1);
  const height =
    allExceptLast.reduce((acc, curr) => acc + curr, 0) + (zoneSensors.length - 1) * gap + 2;

  return height;
}

export default UserCreateStepTwo;
